import { inject, observer } from "mobx-react";
import { useEffect, useRef, useState, useCallback } from "react";

import "./style.scss";

let toastIdCounter = 0;

const normalizeSeverity = (severity) => {
    if (severity === "warning") return "warn";
    if (severity === "danger") return "error";
    return severity || "info";
};

const ToastMessage = ({ message, onClose }) => {
    const timerRef = useRef(null);
    const remainingRef = useRef(message.life);
    const startTimeRef = useRef(null);

    const startTimer = useCallback(() => {
        if (message.sticky) return;
        startTimeRef.current = Date.now();
        timerRef.current = setTimeout(() => {
            onClose(message.id);
        }, remainingRef.current);
    }, [message.id, message.sticky, onClose]);

    const pauseTimer = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
            if (startTimeRef.current) {
                remainingRef.current -= Date.now() - startTimeRef.current;
            }
        }
    }, []);

    useEffect(() => {
        startTimer();
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [startTimer]);

    const severity = normalizeSeverity(message.severity);

    const messageClass = ["p-toast-message", `p-toast-message-${severity}`, message.exiting ? "p-toast-message-exit" : ""].filter(Boolean).join(" ");

    return (
        <div className={messageClass} onMouseEnter={pauseTimer} onMouseLeave={startTimer}>
            <div className="p-toast-message-content">
                <div className="p-toast-message-text">
                    {message.summary && <span className="p-toast-summary">{message.summary}</span>}
                    {message.detail && <div className="p-toast-detail">{message.detail}</div>}
                </div>
                {message.closable && (
                    <button className="p-toast-icon-close" type="button" aria-label="Close" onClick={() => onClose(message.id)}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                            <path d="M13.707.293a1 1 0 010 1.414L8.414 7l5.293 5.293a1 1 0 01-1.414 1.414L7 8.414l-5.293 5.293a1 1 0 01-1.414-1.414L5.586 7 .293 1.707A1 1 0 011.707.293L7 5.586 12.293.293a1 1 0 011.414 0z" />
                        </svg>
                    </button>
                )}
            </div>
        </div>
    );
};

const ToastComponent = inject("mainStore")(
    observer(({ mainStore }) => {
        const toast = mainStore.getMainStore("toast");
        const [messages, setMessages] = useState([]);

        useEffect(() => {
            if (!toast) return;

            const items = Array.isArray(toast) ? toast : [toast];
            const newMessages = items.map((item) => ({
                id: ++toastIdCounter,
                severity: item.severity || "info",
                summary: item.summary || "",
                detail: item.detail || "",
                life: item.life ?? 3000,
                sticky: item.sticky || false,
                closable: item.closable !== false,
                exiting: false,
            }));

            setMessages((prev) => [...prev, ...newMessages]);
            mainStore.setMainStore("toast", null);
        }, [toast]);

        const removeMessage = useCallback((id) => {
            setMessages((prev) => prev.map((msg) => (msg.id === id ? { ...msg, exiting: true } : msg)));
            setTimeout(() => {
                setMessages((prev) => prev.filter((msg) => msg.id !== id));
            }, 300);
        }, []);

        return (
            <div className="p-toast p-toast-top-right toast-component">
                {messages.map((msg) => (
                    <ToastMessage key={msg.id} message={msg} onClose={removeMessage} />
                ))}
            </div>
        );
    }),
);

export default ToastComponent;
